import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { CodeIcon, MapPinIcon, DatabaseIcon, SparklesIcon, UserSearchIcon, MegaphoneIcon, MailIcon, SearchIcon, ArrowRightIcon, ChevronDownIcon, SearchCheckIcon, ZapIcon, SendIcon, ShoppingCartIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { TrustBar } from '../components/TrustBar';
type Category = 'All' | 'Find' | 'Enrich' | 'Outreach';
interface Feature {
  id: string;
  title: string;
  href: string;
  category: Exclude<Category, 'All'>;
  description: string;
  icon: React.ElementType;
  badge?: {
    text: string;
    color: 'blue' | 'orange' | 'green';
  };
}
const features: Feature[] = [{
  id: 'api',
  title: 'Local business API',
  href: '#/local-business-api',
  category: 'Find',
  description: 'The premier local business data API. Search, filter, and access 200M+ local businesses programmatically.',
  icon: CodeIcon
}, {
  id: 'scraper',
  title: 'Google Maps scraper',
  href: '#/google-maps-scraper',
  category: 'Find',
  description: 'Extract business data from Google Maps at scale — contacts, ratings, reviews, hours, and more.',
  icon: MapPinIcon,
  badge: {
    text: 'POPULAR',
    color: 'blue'
  }
}, {
  id: 'database',
  title: 'Local business database',
  href: '#/local-business-database',
  category: 'Find',
  description: 'Download ready-to-use business datasets by category and location. Pre-built datasets updated weekly.',
  icon: DatabaseIcon
}, {
  id: 'enrichment',
  title: 'Local business enrichment',
  href: '#/local-business-enrichment',
  category: 'Enrich',
  description: 'Append missing data points to your existing business lists — emails, phone numbers, social profiles, tech stack.',
  icon: SparklesIcon
}, {
  id: 'owner-finder',
  title: 'Local business owner finder',
  href: '#/owner-finder',
  category: 'Enrich',
  description: 'Find verified decision-maker contacts at any local business. Direct emails, phone numbers, and LinkedIn profiles.',
  icon: UserSearchIcon,
  badge: {
    text: 'HOT',
    color: 'orange'
  }
}, {
  id: 'marketing',
  title: 'Local marketing',
  href: '#/local-marketing',
  category: 'Outreach',
  description: 'Build targeted local business audiences for Meta Ads and direct mail campaigns.',
  icon: MegaphoneIcon
}, {
  id: 'sequencing',
  title: 'Email sequencing',
  href: '#/email-sequencing',
  category: 'Outreach',
  description: 'Create automated multi-step B2B email sequences with built-in personalization and CRM sync.',
  icon: MailIcon
}, {
  id: 'ecommerce-database',
  title: 'E-commerce database',
  href: '#/ecommerce-database',
  category: 'Find',
  description: 'Search 14M+ e-commerce stores with verified owner emails, platform data, and revenue insights.',
  icon: ShoppingCartIcon,
  badge: {
    text: 'NEW',
    color: 'green'
  }
}];
const categories: Category[] = ['All', 'Find', 'Enrich', 'Outreach'];
const workflowTabs = [{
  id: 'find',
  icon: SearchCheckIcon,
  title: '1. Find local businesses',
  description: 'Search our database of 200M+ local businesses or extract data directly from Google Maps to build your initial prospect list.'
}, {
  id: 'enrich',
  icon: ZapIcon,
  title: '2. Enrich with contact data',
  description: 'Append verified owner emails, direct phone numbers, and executive contact details to your business records instantly.'
}, {
  id: 'outreach',
  icon: SendIcon,
  title: '3. Launch outreach campaigns',
  description: 'Use our built-in email sequencing or export custom audiences for Meta and Google Ads to reach decision-makers.'
}];
const faqs = [{
  question: "What's the free trial for Openmart's local business data platform?",
  answer: 'Every new Openmart account gets free credits to explore all features before committing to a paid plan. You can search the database, enrich contacts, and test our APIs — all at no cost. <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" class="text-blue-600 font-semibold hover:underline">Sign up for free →</a>'
}, {
  question: 'How accurate is the local business data?',
  answer: 'We maintain a 95%+ accuracy rate by continuously crawling, verifying, and updating our database of millions of local businesses. Contact data like emails and phone numbers are verified in real-time to ensure your outreach campaigns do not bounce.'
}, {
  question: 'Can I use multiple features together?',
  answer: 'Absolutely. All Openmart features work together seamlessly. For example, you can use the Google Maps Scraper to find businesses, pass them through Local Business Enrichment to find owner emails, and then load them into Email Sequencing for outreach — all within one platform.'
}, {
  question: 'Do you offer an API?',
  answer: 'Yes, our core Local Business API provides programmatic access to our entire 200M+ business database. We also offer specific API endpoints for data enrichment and contact finding, perfect for integrating into your own workflows or CRM.'
}, {
  question: 'What data is included for each business?',
  answer: 'Each business record includes 50+ data fields: company name, address, phone number, website, email, hours of operation, Google rating &amp; reviews, category, social media profiles, tech stack, employee count, revenue estimates, owner/founder name, verified owner email, direct phone number, and LinkedIn profile — all continuously updated and verified.'
}];
// Animated counter hook
function useCounter(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0);
  const countRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(countRef, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number | null = null;
    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration]);
  return {
    count,
    ref: countRef
  };
}
export function Features() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [activeWorkflowTab, setActiveWorkflowTab] = useState(workflowTabs[0].id);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const filteredFeatures = useMemo(() => {
    return features.filter(feature => {
      const matchesSearch = feature.title.toLowerCase().includes(searchQuery.toLowerCase()) || feature.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || feature.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);
  const getCategoryColor = (category: Exclude<Category, 'All'>) => {
    switch (category) {
      case 'Find':
        return 'bg-blue-50 text-blue-700';
      case 'Enrich':
        return 'bg-green-50 text-green-700';
      case 'Outreach':
        return 'bg-purple-50 text-purple-700';
    }
  };
  const getBadgeColor = (color: 'blue' | 'orange' | 'green') => {
    switch (color) {
      case 'blue':
        return 'bg-blue-600 text-white';
      case 'orange':
        return 'bg-orange-500 text-white';
      case 'green':
        return 'bg-green-500 text-white';
    }
  };
  return <div className="min-h-screen bg-slate-50/50 selection:bg-blue-100 selection:text-blue-900 flex flex-col ecom-aeonik" style={{
    fontFamily: "'Aeonik', system-ui, sans-serif"
  }} data-id="element-6377">
      <Navbar data-id="element-6378" />

      <main className="flex-grow pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-6379">
        {/* Hero Section - Apollo Style Contained */}
        <section className="relative bg-white rounded-[2.5rem] overflow-hidden shadow-sm border border-slate-100 pt-28 pb-20 px-8" data-id="element-6380">
          {/* Single subtle background blob */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30" data-id="element-6381">
            <div className="absolute top-[-10%] left-[10%] w-[60%] h-[60%] rounded-full bg-blue-100 blur-[120px] animate-aurora-blob-1" data-id="element-6382"></div>
          </div>

          {/* Subtle dot pattern overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, #156ef6 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-6383"></div>

          <div className="max-w-5xl mx-auto flex flex-col items-center text-center relative z-10" data-id="element-6384">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: 'easeOut'
          }} data-id="element-6385">
              <h1 className="text-5xl md:text-6xl text-slate-900 mb-6 tracking-tight leading-[1.1]" style={{
              fontWeight: 700
            }} data-id="element-6386">
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="inline" data-id="element-6387">
                  Explore
                </motion.span>{' '}
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="inline text-blue-600" data-id="element-6388">
                  local business
                </motion.span>{' '}
                <motion.span initial={{
                opacity: 0,
                y: 20,
                filter: 'blur(10px)'
              }} animate={{
                opacity: 1,
                y: 0,
                filter: 'blur(0px)'
              }} transition={{
                duration: 0.6,
                delay: 0.3
              }} className="inline" data-id="element-6389">
                  features
                </motion.span>
              </h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed" data-id="element-6390">
                Find, enrich, and reach 200M+ local businesses. The complete
                platform for local business data.
              </motion.p>
            </motion.div>

            <div className="flex items-center justify-center gap-4 flex-wrap mt-10" data-id="element-6391">
              {[{
              label: '200M+',
              text: 'businesses',
              delay: 0.5
            }, {
              label: '50+',
              text: 'countries',
              delay: 0.6
            }, {
              label: '99.5%',
              text: 'uptime',
              delay: 0.7
            }].map((stat, i) => <motion.span key={i} initial={{
              opacity: 0,
              y: 20,
              scale: 0.8
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: stat.delay,
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} className="text-base font-medium text-slate-500 bg-white/50 backdrop-blur-sm px-4 py-1.5 rounded-full border border-slate-200/50" data-id="element-6392">
                  <span className="text-blue-600 font-semibold" data-id="element-6393">
                    {stat.label}
                  </span>{' '}
                  {stat.text}
                </motion.span>)}
            </div>

            <motion.a initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.8
          }} href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 hover:scale-105 transition-all duration-300 text-base shadow-lg shadow-blue-600/20 relative group" data-id="element-6394">
              <motion.div animate={{
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: 'reverse'
            }} className="absolute inset-0 bg-blue-400 rounded-full opacity-0 group-hover:opacity-20 blur-xl" data-id="element-6395"></motion.div>
              <span className="relative z-10 flex items-center gap-2" data-id="element-6396">
                Try for free <ArrowRightIcon className="w-4 h-4" data-id="element-6397" />
              </span>
            </motion.a>
          </div>
        </section>

        {/* Search & Filters Section - Floating Glassmorphic Bar */}
        <motion.section initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="relative z-20 -mt-12 mb-12" data-id="element-6398">
          <div className="max-w-4xl mx-auto px-4" data-id="element-6399">
            <div className="bg-white/60 backdrop-blur-xl border border-white/80 shadow-xl rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-4" data-id="element-6400">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="relative flex-grow" data-id="element-6401">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-6402">
                  <SearchIcon className="h-5 w-5 text-slate-400" data-id="element-6403" />
                </div>
                <input type="text" className="block w-full pl-11 pr-4 py-3 border border-slate-200/60 rounded-xl bg-white/80 backdrop-blur-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-base shadow-sm" placeholder="Search features..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6404" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="flex flex-wrap gap-2" data-id="element-6405">
                {categories.map((category, index) => <motion.button key={category} onClick={() => setActiveCategory(category)} whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: 0.3 + index * 0.05
              }} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${activeCategory === category ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' : 'bg-white/80 text-slate-600 hover:bg-white border border-slate-200/60 hover:border-slate-300'}`} data-id="element-6406">
                    {category}
                  </motion.button>)}
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Features Grid Section - Apollo Style Contained */}
        <section className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-slate-100" data-id="element-6407">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="flex items-center justify-between mb-10" data-id="element-6408">
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-6409">All features</h2>
            <motion.span key={filteredFeatures.length} initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            stiffness: 200
          }} className="text-sm font-semibold text-slate-500 bg-slate-100 px-4 py-1.5 rounded-full" data-id="element-6410">
              {filteredFeatures.length}{' '}
              {filteredFeatures.length === 1 ? 'feature' : 'features'}
            </motion.span>
          </motion.div>

          <AnimatePresence mode="wait" data-id="element-6411">
            {filteredFeatures.length > 0 ? <motion.div key={activeCategory} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6412">
                {filteredFeatures.map((feature, index) => <motion.a key={feature.id} href={feature.href} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.1,
              ease: 'easeOut'
            }} whileHover={{
              y: -8,
              transition: {
                duration: 0.2
              }
            }} className="group flex flex-col bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-2xl hover:shadow-blue-600/10 hover:border-blue-200 transition-all duration-300 cursor-pointer h-full relative overflow-hidden" data-id="element-6413">
                    <div className="absolute inset-0 bg-blue-50/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-6414"></div>

                    <div className="flex items-start justify-between mb-6 relative z-10" data-id="element-6415">
                      <motion.div animate={{
                  scale: [1, 1.05, 1]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  repeatType: 'reverse'
                }} className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center group-hover:bg-blue-50 transition-colors" data-id="element-6416">
                        <motion.div whileHover={{
                    rotate: 15,
                    scale: 1.1
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-6417">
                          <feature.icon className="w-6 h-6 text-slate-700 group-hover:text-blue-600 transition-colors" data-id="element-6418" />
                        </motion.div>
                      </motion.div>
                      {feature.badge && <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${getBadgeColor(feature.badge.color)}`} data-id="element-6419">
                          {feature.badge.text}
                        </span>}
                    </div>

                    <div className="flex-grow relative z-10" data-id="element-6420">
                      <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors" data-id="element-6421">
                        {feature.title}
                      </h3>
                      <span className={`inline-block px-3 py-1 rounded-lg text-xs font-semibold mb-4 ${getCategoryColor(feature.category)}`} data-id="element-6422">
                        {feature.category}
                      </span>
                      <p className="text-sm text-slate-500 leading-relaxed line-clamp-3" data-id="element-6423">
                        {feature.description}
                      </p>
                    </div>

                    <div className="mt-6 pt-5 border-t border-slate-100 flex items-center text-sm font-semibold text-blue-600 group-hover:text-blue-700 transition-colors relative z-10" data-id="element-6424">
                      Learn more{' '}
                      <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-6425" />
                    </div>
                  </motion.a>)}
              </motion.div> : <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3
          }} className="text-center py-32 bg-slate-50 rounded-3xl border border-slate-100" data-id="element-6426">
                <SearchIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-6427" />
                <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6428">
                  No features found
                </h3>
                <p className="text-slate-500" data-id="element-6429">
                  We couldn't find any features matching your search. Try
                  adjusting your filters.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setActiveCategory('All');
            }} className="mt-6 px-6 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-colors" data-id="element-6430">
                  Clear filters
                </button>
              </motion.div>}
          </AnimatePresence>
        </section>

        {/* Workflow Section - simplified background */}
        <section className="bg-slate-50 rounded-[2.5rem] p-8 md:p-16 shadow-sm border border-slate-100" data-id="element-6431">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-6432">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-6433">
              The complete{' '}
              <span className="text-blue-600" data-id="element-6434">local business data</span>{' '}
              pipeline
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed" data-id="element-6435">
              All features work together seamlessly, taking you from raw search
              to booked meetings in one platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-6436">
            {/* Left: Tabs */}
            <div className="lg:col-span-5 space-y-3 relative" data-id="element-6437">
              {/* Connecting line */}
              <div className="absolute left-6 top-16 bottom-16 w-0.5 bg-slate-200 hidden lg:block" data-id="element-6438"></div>

              {workflowTabs.map((tab, index) => {
              const isActive = activeWorkflowTab === tab.id;
              return <motion.button key={tab.id} onClick={() => setActiveWorkflowTab(tab.id)} initial={{
                opacity: 0,
                x: -30
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} whileHover={{
                x: 4
              }} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 relative ${isActive ? 'bg-white shadow-lg border border-slate-200' : 'bg-transparent hover:bg-white/50 border border-transparent'}`} data-id="element-6439">
                    <div className="flex items-start gap-6" data-id="element-6440">
                      <motion.div animate={isActive ? {
                    scale: [1, 1.1, 1]
                  } : {}} transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: 'reverse'
                  }} className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 relative z-10 ${isActive ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-500'}`} data-id="element-6441">
                        <tab.icon className="w-6 h-6" data-id="element-6442" />
                      </motion.div>
                      <div data-id="element-6443">
                        <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-6444">
                          {tab.title}
                        </h3>
                        <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-6445">
                          {tab.description}
                        </p>
                      </div>
                    </div>
                  </motion.button>;
            })}
            </div>

            {/* Right: Visuals */}
            <div className="lg:col-span-7" data-id="element-6446">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="bg-white rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden border border-slate-200 shadow-xl" data-id="element-6447">
                <AnimatePresence mode="wait" data-id="element-6448">
                  {activeWorkflowTab === 'find' && <motion.div key="find" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3
                }} className="w-full max-w-md relative z-10" data-id="element-6449">
                      <motion.div initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.1
                  }} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 mb-4" data-id="element-6450">
                        <div className="flex items-center gap-3 mb-4" data-id="element-6451">
                          <SearchIcon className="w-5 h-5 text-slate-400" data-id="element-6452" />
                          <div className="h-2 w-32 bg-slate-200 rounded-full" data-id="element-6453"></div>
                        </div>
                        <div className="space-y-3" data-id="element-6454">
                          {[1, 2, 3].map(i => <motion.div key={i} initial={{
                        opacity: 0,
                        x: -20
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        delay: 0.2 + i * 0.1
                      }} className="bg-white p-4 rounded-xl border border-slate-100 flex items-center gap-4 hover:shadow-md transition-shadow" data-id="element-6455">
                              <div className="w-10 h-10 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-6456"></div>
                              <div className="space-y-2 flex-grow" data-id="element-6457">
                                <div className="h-2 w-24 bg-slate-200 rounded-full" data-id="element-6458"></div>
                                <div className="h-2 w-16 bg-slate-100 rounded-full" data-id="element-6459"></div>
                              </div>
                            </motion.div>)}
                        </div>
                      </motion.div>
                      <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.5
                  }} className="text-center text-sm font-semibold text-blue-600 bg-blue-50 py-3 rounded-xl" data-id="element-6460">
                        200M+ businesses indexed
                      </motion.div>
                    </motion.div>}

                  {activeWorkflowTab === 'enrich' && <motion.div key="enrich" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3
                }} className="w-full max-w-md relative z-10" data-id="element-6461">
                      <div className="space-y-4" data-id="element-6462">
                        {[{
                      label: 'Verified emails',
                      value: 'sarah@company.com',
                      color: 'text-green-600',
                      bg: 'bg-green-50'
                    }, {
                      label: 'Direct phones',
                      value: '+1 (415) 555-0192',
                      color: 'text-blue-600',
                      bg: 'bg-blue-50'
                    }, {
                      label: 'Social profiles',
                      value: 'linkedin.com/in/sarah',
                      color: 'text-purple-600',
                      bg: 'bg-purple-50'
                    }].map((item, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: 0.1 + i * 0.1
                    }} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow" data-id="element-6463">
                            <span className="text-sm font-bold text-slate-700" data-id="element-6464">
                              {item.label}
                            </span>
                            <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${item.color} ${item.bg}`} data-id="element-6465">
                              {item.value}
                            </span>
                          </motion.div>)}
                      </div>
                    </motion.div>}

                  {activeWorkflowTab === 'outreach' && <motion.div key="outreach" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.3
                }} className="w-full max-w-md relative z-10" data-id="element-6466">
                      <motion.div initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} transition={{
                    delay: 0.1
                  }} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm relative" data-id="element-6467">
                        <motion.div initial={{
                      opacity: 0,
                      scale: 0
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} transition={{
                      delay: 0.3,
                      type: 'spring'
                    }} className="absolute -top-3 -right-3 bg-green-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm" data-id="element-6468">
                          Sent
                        </motion.div>
                        <div className="border-b border-slate-100 pb-5 mb-5" data-id="element-6469">
                          <div className="text-xs font-bold text-slate-400 uppercase mb-2" data-id="element-6470">
                            Subject
                          </div>
                          <div className="text-sm font-semibold text-slate-900" data-id="element-6471">
                            Quick question about {'{{company}}'}
                          </div>
                        </div>
                        <div className="space-y-3" data-id="element-6472">
                          {[0.75, 1, 0.85, 0.5].map((width, i) => <motion.div key={i} initial={{
                        width: 0
                      }} animate={{
                        width: `${width * 100}%`
                      }} transition={{
                        delay: 0.2 + i * 0.1,
                        duration: 0.4
                      }} className="h-2 bg-slate-100 rounded-full" data-id="element-6473"></motion.div>)}
                        </div>
                        <div className="mt-8 flex gap-3" data-id="element-6474">
                          <motion.div initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.6
                      }} className="flex-1 bg-slate-50 rounded-xl py-3 text-center text-xs font-semibold text-slate-500" data-id="element-6475">
                            <span className="block text-xl text-slate-900 mb-1" data-id="element-6476">
                              45%
                            </span>{' '}
                            Open rate
                          </motion.div>
                          <motion.div initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        delay: 0.7
                      }} className="flex-1 bg-slate-50 rounded-xl py-3 text-center text-xs font-semibold text-slate-500" data-id="element-6477">
                            <span className="block text-xl text-slate-900 mb-1" data-id="element-6478">
                              12%
                            </span>{' '}
                            Reply rate
                          </motion.div>
                        </div>
                      </motion.div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section - Apollo Style Contained */}
        <StatsSection data-id="element-6479" />

        {/* FAQ Section - Apollo Style Contained */}
        <section className="bg-white rounded-[2.5rem] p-8 md:p-16 shadow-sm border border-slate-100" data-id="element-6480">
          <div className="max-w-3xl mx-auto" data-id="element-6481">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-6482">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-6483">
                Frequently asked{' '}
                <span className="text-blue-600" data-id="element-6484">questions</span>
              </h2>
            </motion.div>

            <div className="space-y-4" data-id="element-6485">
              {faqs.map((faq, index) => {
              const isOpen = openFaqIndex === index;
              return <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} transition={{
                duration: 0.5,
                delay: index * 0.1
              }} className={`rounded-2xl border transition-colors duration-300 ${isOpen ? 'bg-slate-50 border-slate-200' : 'bg-transparent border-slate-100 hover:border-slate-200'}`} data-id="element-6486">
                    <button onClick={() => setOpenFaqIndex(isOpen ? null : index)} className="w-full text-left flex justify-between items-center focus:outline-none group p-6" aria-expanded={isOpen} data-id="element-6487">
                      <span className={`text-lg font-semibold transition-colors pr-8 ${isOpen ? 'text-blue-600' : 'text-slate-900 group-hover:text-blue-600'}`} data-id="element-6488">
                        {faq.question}
                      </span>
                      <motion.div animate={{
                    rotate: isOpen ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200 group-hover:text-blue-600'}`} data-id="element-6489">
                        <ChevronDownIcon className="w-5 h-5 flex-shrink-0" data-id="element-6490" />
                      </motion.div>
                    </button>
                    <AnimatePresence data-id="element-6491">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-6492">
                          <div className="px-6 pb-6 text-base text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{
                      __html: faq.answer
                    }} data-id="element-6493" />
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>;
            })}
            </div>
          </div>
        </section>

        {/* CTA Section - simplified to solid blue */}
        <section className="bg-blue-600 rounded-[2.5rem] relative overflow-hidden shadow-xl" data-id="element-6494">
          <div className="max-w-4xl mx-auto px-8 py-24 relative z-10 text-center" data-id="element-6495">
            <motion.h2 initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-6496">
              Ready to scale your local business outreach?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-xl text-white/80 mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-6497">
              Get free credits when you sign up. Explore all of Openmart's
              features risk-free — no credit card required.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6498">
              <motion.a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="inline-flex items-center gap-2 px-10 py-5 bg-white text-blue-600 font-bold rounded-full hover:bg-blue-50 transition-colors text-lg w-full sm:w-auto justify-center shadow-xl" data-id="element-6499">
                Try for free
              </motion.a>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-6500" />
    </div>;
}
// Stats Section Component with Animated Counters
function StatsSection() {
  const stats = [{
    end: 200,
    suffix: 'M+',
    label: 'Local businesses'
  }, {
    end: 50,
    suffix: '+',
    label: 'Data fields'
  }, {
    end: 95,
    suffix: '%',
    label: 'Data accuracy'
  }, {
    end: 100,
    suffix: '%',
    label: 'Real-time verification'
  }];
  return <section className="bg-white rounded-[2.5rem] p-12 md:p-16 shadow-sm border border-slate-100" data-id="element-6501">
      <div className="max-w-6xl mx-auto" data-id="element-6502">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 text-center" data-id="element-6503">
          {stats.map((stat, index) => <StatCounter key={index} stat={stat} index={index} data-id="element-6504" />)}
        </div>
      </div>
    </section>;
}
function StatCounter({
  stat,
  index
}: {
  stat: {
    end: number;
    suffix: string;
    label: string;
  };
  index: number;
}) {
  const {
    count,
    ref
  } = useCounter(stat.end, 2000);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.6,
    delay: index * 0.1
  }} data-id="element-6505">
      <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3" data-id="element-6506">
        {count}
        {stat.suffix}
      </div>
      <div className="text-sm font-semibold text-slate-500 tracking-wider" data-id="element-6507">
        {stat.label}
      </div>
    </motion.div>;
}